<script setup lang="ts">
import { ref } from 'vue'
import { useRoute } from 'vue-router'

import { ChevronDown } from 'lucide-vue-next'
import type { Component } from 'vue'
import { useDark } from '@vueuse/core'

interface Props {
  icon: Component
  isExpanded: boolean
  label: string
  route?: string
  isActive?: boolean
  children?: { title: string; path: string; icon?: Component }[]
}

const props = defineProps<Props>()

const emit = defineEmits<{
  click: []
  openFloatingMenu: [
    event: MouseEvent,
    children: { title: string; path: string; icon?: Component }[],
  ]
}>()
const isDark = useDark({ selector: 'html' })
const currentRoute = useRoute()
const isSubmenuOpen = ref(false)

const handleClick = (event?: MouseEvent) => {
  if (props.children) {
    if (props.isExpanded) {
      isSubmenuOpen.value = !isSubmenuOpen.value
    } else if (event) {
      emit('openFloatingMenu', event, props.children)
    }
  } else {
    emit('click')
  }
}

const isRouteActive = () => {
  const currentPath = currentRoute.path

  // Si el item tiene hijos, verificar si algún hijo está activo o si la ruta actual comienza con el path del hijo
  if (props.children) {
    return props.children.some(
      (child) => currentPath === child.path || currentPath.startsWith(child.path + '/'),
    )
  }

  // Si no tiene hijos, comparar directamente o verificar si es una ruta anidada
  if (props.route) {
    return currentPath === props.route || currentPath.startsWith(props.route + '/')
  }

  return false
}

const isChildActive = (childPath: string) => {
  const currentPath = currentRoute.path
  return currentPath === childPath || currentPath.startsWith(childPath + '/')
}

const sidebarOptionClasses = (active: boolean) => {
  if (active) {
    return ['sidebar-option-active', isDark.value ? 'sidebar-option-active-dark' : '']
  }

  return ['sidebar-option-idle', isDark.value ? 'sidebar-option-idle-dark' : '']
}
</script>

<template>
  <li class="w-full">
    <!-- Router link para navegación sin hijos -->
    <router-link
      v-if="route && !children"
      :to="route"
      class="sidebar-option group flex items-center gap-3 mb-1 p-2.5 rounded-lg border border-transparent transition-colors duration-150 ease-in-out"
      :class="[{ 'justify-center': !isExpanded }, sidebarOptionClasses(isRouteActive())]"
    >
      <component
        :is="icon"
        :size="20"
        class="shrink-0 transition-colors duration-150"
        :class="
          isRouteActive()
            ? 'text-(--p-primary-color)'
            : 'text-slate-500 group-hover:text-(--p-primary-color) dark:text-slate-400 dark:group-hover:text-slate-100'
        "
      />
      <span
        v-if="isExpanded"
        class="text-sm quicksand transition-colors duration-150 truncate"
        :class="
          isRouteActive()
            ? 'font-semibold text-(--p-primary-color)'
            : 'font-medium text-slate-600 group-hover:text-(--p-primary-color) dark:text-slate-300 dark:group-hover:text-white'
        "
      >
        {{ label }}
      </span>
    </router-link>
    <hr
      v-if="route && !children"
      class="mx-auto border-0 border-b border-b-slate-200 w-8/9 opacity-80 dark:border-b-slate-700"
    />

    <!-- Elemento con hijos (submenu) -->
    <div v-else-if="children" class="w-full">
      <button
        @click="handleClick($event)"
        class="sidebar-option group flex items-center gap-3 w-full p-2.5 rounded-lg border border-transparent transition-colors duration-150 ease-in-out cursor-pointer"
        :class="[{ 'justify-center': !isExpanded }, sidebarOptionClasses(isRouteActive())]"
      >
        <component
          :is="icon"
          :size="20"
          :stroke-width="1.9"
          class="shrink-0 transition-colors duration-150"
          :class="
            isRouteActive()
              ? 'text-(--p-primary-color)'
              : 'text-slate-500 group-hover:text-(--p-primary-color) dark:text-slate-400 dark:group-hover:text-slate-100'
          "
        />
        <span
          v-if="isExpanded"
          class="text-sm flex-1 text-left transition-colors duration-150 truncate"
          :class="
            isRouteActive()
              ? 'font-semibold text-(--p-primary-color)'
              : 'font-medium text-slate-600 group-hover:text-(--p-primary-color) dark:text-slate-300 dark:group-hover:text-white'
          "
        >
          {{ label }}
        </span>
        <ChevronDown
          v-if="isExpanded"
          :size="15"
          class="transition-transform duration-150 text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300"
          :class="{ 'rotate-180': isSubmenuOpen }"
        />
      </button>

      <!-- Submenu expandido (solo cuando el sidebar está expandido) -->
      <div v-if="isExpanded" class="overflow-hidden">
        <transition
          enter-active-class="transition-all duration-150 ease-out"
          leave-active-class="transition-all duration-120 ease-in"
          enter-from-class="opacity-0 max-h-0"
          enter-to-class="opacity-100 max-h-96"
          leave-from-class="opacity-100 max-h-96"
          leave-to-class="opacity-0 max-h-0"
        >
          <ul v-show="isSubmenuOpen" class="mt-1 ml-7 space-y-1">
            <li v-for="child in children" :key="child.path">
              <router-link
                :to="child.path"
                class="sidebar-option group flex items-center gap-2 p-2 pl-3 rounded-md border border-transparent transition-colors duration-150"
                :class="
                  isChildActive(child.path)
                    ? [...sidebarOptionClasses(true), 'text-(--p-primary-color)']
                    : 'sidebar-option-idle text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
                "
              >
                <component
                  :is="child.icon"
                  v-if="child.icon"
                  :size="14"
                  :stroke-width="1.8"
                  class="shrink-0 transition-colors duration-150"
                />
                <div
                  v-else
                  class="w-1.5 h-1.5 rounded-full transition-colors duration-150"
                  :class="
                    isChildActive(child.path)
                      ? 'bg-(--p-primary-color)'
                      : 'bg-slate-400 group-hover:bg-slate-700 dark:bg-slate-500 dark:group-hover:bg-slate-200'
                  "
                ></div>
                <span class="text-xs font-medium truncate">{{ child.title }}</span>
              </router-link>
            </li>
          </ul>
        </transition>
      </div>
    </div>

    <!-- Botón genérico sin ruta ni hijos -->
    <button
      v-else
      @click="handleClick()"
      class="sidebar-option sidebar-option-idle group flex items-center gap-3 w-full p-2.5 rounded-lg border border-transparent transition-colors duration-150 ease-in-out cursor-pointer"
      :class="[{ 'justify-center': !isExpanded }]"
    >
      <component
        :is="icon"
        :size="20"
        :stroke-width="1.9"
        class="shrink-0 text-slate-500 transition-colors duration-150 group-hover:text-(--p-primary-color) dark:text-slate-400 dark:group-hover:text-slate-100"
      />
      <span
        v-if="isExpanded"
        class="text-sm font-medium text-slate-600 transition-colors duration-150 truncate group-hover:text-slate-900 dark:text-slate-300 dark:group-hover:text-white"
      >
        {{ label }}
      </span>
    </button>
  </li>
</template>

<style scoped>
.sidebar-option {
  transition:
    background-color 140ms ease,
    border-color 140ms ease,
    color 140ms ease;
}

.sidebar-option-idle:hover {
  background: rgba(15, 23, 42, 0.045);
  border-color: rgba(15, 23, 42, 0.04);
}

.sidebar-option-active {
  border-color: color-mix(in srgb, var(--p-primary-color) 24%, transparent);
  background: color-mix(in srgb, var(--p-primary-color) 10%, transparent);
}

.sidebar-option-idle-dark:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.04);
}

.sidebar-option-active-dark {
  border-color: color-mix(in srgb, var(--p-primary-color) 32%, transparent);
  background: color-mix(in srgb, var(--p-primary-color) 14%, transparent);
}
</style>
